import { useState, useCallback, useEffect } from "react";
import { ScrollView, RefreshControl, ActivityIndicator, Pressable, View, Text } from "react-native";
import { useLocalSearchParams } from "expo-router";
import { collectionGroup, query, where, getDocs, doc, getDoc } from "firebase/firestore";
import { httpsCallable } from "firebase/functions";
import { db, functions } from "@/lib/firebase";
import { useClub } from "@/contexts/ClubContext";
import ContentWidth from "@/components/ContentWidth";
import { MemberDetailSkeleton } from "@/components/Skeleton";
import { colors, radius, font } from "@/lib/theme";
import type { Fee, Member } from "@/lib/types";
import styled from "styled-components/native";
import { useAlert } from "@/hooks/useAlert";
import AlertSheet from "@/components/AlertSheet";

const Hero = styled.View`
  background-color: ${colors.amber50};
  border-radius: ${radius.lg}px;
  border: 1px solid ${colors.amber200};
  padding: 20px;
  margin: 20px 20px 0;
  flex-direction: row;
  align-items: center;
  gap: 16px;
`;

const HeroAvatar = styled.View`
  width: 56px;
  height: 56px;
  border-radius: 28px;
  background-color: ${colors.amber};
  align-items: center;
  justify-content: center;
`;

const HeroAvatarText = styled.Text`
  font-size: 22px;
  font-weight: ${font.extrabold};
  color: #fff;
`;

const HeroName = styled.Text`
  font-size: 18px;
  font-weight: ${font.extrabold};
  color: ${colors.ink};
`;

const HeroOwed = styled.Text`
  font-size: 26px;
  font-weight: ${font.extrabold};
  color: ${colors.amber};
  margin-top: 2px;
  letter-spacing: -0.5px;
`;

const Section = styled.View`
  padding: 24px 20px 0;
`;

const SectionLabel = styled.Text`
  font-size: 12px;
  font-weight: ${font.extrabold};
  color: ${colors.inkMuted};
  text-transform: uppercase;
  letter-spacing: 1px;
  margin-bottom: 12px;
`;

const Card = styled.View`
  margin: 0 20px;
  background-color: ${colors.white};
  border-radius: ${radius.lg}px;
  border: 1px solid ${colors.line};
  overflow: hidden;
`;

const Row = styled.View<{ hasBorder?: boolean }>`
  flex-direction: row;
  align-items: center;
  padding: 14px 16px;
  ${(p) => p.hasBorder && `border-bottom-width: 1px; border-bottom-color: ${colors.line};`}
`;

const RowMain = styled.View`
  flex: 1;
`;

const RowTitle = styled.Text`
  font-size: 14px;
  font-weight: ${font.bold};
  color: ${colors.ink};
`;

const RowSub = styled.Text`
  font-size: 12px;
  color: ${colors.inkMuted};
  margin-top: 2px;
`;

const PendingBadge = styled.View`
  background-color: ${colors.amber50};
  border: 1px solid ${colors.amber200};
  border-radius: ${radius.sm}px;
  padding: 4px 10px;
`;

const PendingText = styled.Text`
  font-size: 12px;
  font-weight: ${font.bold};
  color: ${colors.amber};
`;

const PaidBadge = styled.View`
  background-color: ${colors.brand50};
  border: 1px solid ${colors.brandLight};
  border-radius: ${radius.sm}px;
  padding: 4px 10px;
`;

const PaidText = styled.Text`
  font-size: 12px;
  font-weight: ${font.bold};
  color: ${colors.brandDark};
`;

const RemindBtnLarge = styled.View`
  background-color: ${colors.brand};
  border-radius: ${radius.card}px;
  padding: 16px;
  align-items: center;
  margin: 24px 20px 0;
`;

const RemindBtnText = styled.Text`
  color: #fff;
  font-size: 16px;
  font-weight: ${font.bold};
`;

const EmptyText = styled.Text`
  font-size: 13px;
  color: ${colors.inkMuted};
  text-align: center;
  padding: 20px;
`;

function pence(p: number): string {
  return `\u00A3${(p / 100).toFixed(2)}`;
}

function formatDate(s: string): string {
  try {
    const d = new Date(s + "T00:00:00");
    return d.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
  } catch {
    return s;
  }
}

interface FeeRow extends Fee {
  id: string;
}

export default function FinancesPlayerScreen() {
  const params = useLocalSearchParams<{ userId: string }>();
  const { club } = useClub();
  const { alertProps, alert } = useAlert();
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [reminding, setReminding] = useState(false);
  const [member, setMember] = useState<Member | null>(null);
  const [fees, setFees] = useState<FeeRow[]>([]);

  const loadData = useCallback(async () => {
    if (!club || !params.userId) return;
    try {
      const memberDoc = await getDoc(doc(db, "clubs", club.id, "members", params.userId));
      if (memberDoc.exists()) {
        setMember(memberDoc.data() as Member);
      }

      const feeQuery = query(
        collectionGroup(db, "fees"),
        where("clubId", "==", club.id),
        where("userId", "==", params.userId)
      );
      const feeSnap = await getDocs(feeQuery);
      const allFees = feeSnap.docs.map((d) => ({ id: d.id, ...d.data() })) as FeeRow[];
      allFees.sort((a, b) => (b.matchDate || "").localeCompare(a.matchDate || ""));
      setFees(allFees);
    } catch (err) {
      console.error("Failed to load player finances:", err);
    }
  }, [club?.id, params.userId]);

  useEffect(() => {
    setLoading(true);
    loadData().finally(() => setLoading(false));
  }, [loadData]);

  const handleRefresh = useCallback(async () => {
    setRefreshing(true);
    await loadData();
    setRefreshing(false);
  }, [loadData]);

  if (loading) {
    return (
      <View style={{ flex: 1, backgroundColor: colors.bg }}>
        <MemberDetailSkeleton />
      </View>
    );
  }

  if (!member) {
    return (
      <View style={{ flex: 1, backgroundColor: colors.bg, justifyContent: "center", alignItems: "center" }}>
        <Text style={{ color: colors.inkMuted }}>Member not found</Text>
      </View>
    );
  }

  const isMatch = (f: FeeRow) => (f.feeType ?? "match") === "match";
  const isTraining = (f: FeeRow) => f.feeType === "training";
  const unpaidMatch = fees.filter((f) => !f.paid && isMatch(f));
  const paidMatch = fees.filter((f) => f.paid && isMatch(f));
  const unpaidTraining = fees.filter((f) => !f.paid && isTraining(f));
  const paidTraining = fees.filter((f) => f.paid && isTraining(f));
  const unpaidFees = [...unpaidMatch, ...unpaidTraining];
  const paidFees = [...paidMatch, ...paidTraining];
  const matchFeesOwed = unpaidFees.reduce((s, f) => s + (f.amount || 0), 0);
  const membershipOwed = member.membership
    ? Math.max(0, member.membership.totalAmount - member.membership.paidAmount)
    : 0;
  const totalOwed = matchFeesOwed + membershipOwed;
  const totalPaid = paidFees.reduce((s, f) => s + (f.amount || 0), 0);

  const handleRemind = async () => {
    if (!club) return;
    setReminding(true);
    try {
      const sendReminder = httpsCallable(functions, "sendFeeReminder");
      await sendReminder({ clubId: club.id, userId: params.userId });
      await alert({
        title: "Reminder sent",
        emoji: "✉️",
        message: `${member.displayName} has been emailed about their outstanding fees.`,
      });
    } catch (err: any) {
      await alert({
        title: "Failed to send",
        message: err?.message || "Try again later.",
        emoji: "❌",
        buttonColor: colors.danger,
      });
    } finally {
      setReminding(false);
    }
  };

  return (
    <ScrollView
      style={{ flex: 1, backgroundColor: colors.bg }}
      showsVerticalScrollIndicator={false}
      refreshControl={<RefreshControl refreshing={refreshing} onRefresh={handleRefresh} tintColor={colors.brand} />}
    >
      <ContentWidth>
        {/* Hero */}
        {totalOwed > 0 ? (
          <Hero>
            <HeroAvatar>
              <HeroAvatarText>{member.displayName?.[0]?.toUpperCase() || "?"}</HeroAvatarText>
            </HeroAvatar>
            <View style={{ flex: 1 }}>
              <HeroName>{member.displayName}</HeroName>
              <HeroOwed>{pence(totalOwed)} owed</HeroOwed>
            </View>
          </Hero>
        ) : (
          <View style={{
            backgroundColor: colors.brand50,
            borderRadius: radius.lg,
            borderWidth: 1,
            borderColor: colors.brandLight,
            padding: 20,
            margin: 20,
            flexDirection: "row",
            alignItems: "center",
            gap: 16,
          }}>
            <View style={{ width: 56, height: 56, borderRadius: 28, backgroundColor: colors.brand, alignItems: "center", justifyContent: "center" }}>
              <Text style={{ fontSize: 22, color: "#fff", fontWeight: "800" }}>{member.displayName?.[0]?.toUpperCase() || "?"}</Text>
            </View>
            <View style={{ flex: 1 }}>
              <HeroName>{member.displayName}</HeroName>
              <Text style={{ fontSize: 14, color: colors.brandDark, fontWeight: "700", marginTop: 4 }}>
                ✓ All paid up
              </Text>
            </View>
          </View>
        )}

        {/* Profile */}
        <Section>
          <SectionLabel>Profile</SectionLabel>
        </Section>
        <Card>
          <Row hasBorder>
            <RowMain>
              <RowSub>Email</RowSub>
              <RowTitle style={{ marginTop: 2 }}>{member.email || "—"}</RowTitle>
            </RowMain>
          </Row>
          {member.phone && (
            <Row hasBorder>
              <RowMain>
                <RowSub>Phone</RowSub>
                <RowTitle style={{ marginTop: 2 }}>{member.phone}</RowTitle>
              </RowMain>
            </Row>
          )}
          <Row hasBorder>
            <RowMain>
              <RowSub>Role</RowSub>
              <RowTitle style={{ marginTop: 2, textTransform: "capitalize" }}>{member.role || "player"}</RowTitle>
            </RowMain>
          </Row>
          {member.teams && member.teams.length > 0 && (
            <Row hasBorder>
              <RowMain>
                <RowSub>Teams</RowSub>
                <RowTitle style={{ marginTop: 2 }}>{member.teams.join(", ")}</RowTitle>
              </RowMain>
            </Row>
          )}
          {member.emergencyContactName && (
            <Row hasBorder={!!member.medicalConditions}>
              <RowMain>
                <RowSub>Emergency contact</RowSub>
                <RowTitle style={{ marginTop: 2 }}>
                  {member.emergencyContactName}
                  {member.emergencyContactPhone ? ` · ${member.emergencyContactPhone}` : ""}
                </RowTitle>
              </RowMain>
            </Row>
          )}
          {member.medicalConditions && (
            <Row>
              <RowMain>
                <RowSub>Medical conditions</RowSub>
                <RowTitle style={{ marginTop: 2 }}>{member.medicalConditions}</RowTitle>
              </RowMain>
            </Row>
          )}
        </Card>

        {/* Membership */}
        <Section>
          <SectionLabel>Membership</SectionLabel>
        </Section>
        <Card>
          <View style={{ padding: 16 }}>
            <View style={{ flexDirection: "row", justifyContent: "space-between", alignItems: "center" }}>
              <RowTitle>Annual Membership</RowTitle>
              {!member.membership ? (
                <PendingBadge><PendingText>Not paid</PendingText></PendingBadge>
              ) : membershipOwed > 0 ? (
                <PendingBadge><PendingText>{pence(membershipOwed)} owed</PendingText></PendingBadge>
              ) : (
                <PaidBadge><PaidText>Paid in full</PaidText></PaidBadge>
              )}
            </View>
            {member.membership && (
              <RowSub style={{ marginTop: 6 }}>
                {pence(member.membership.paidAmount)} of {pence(member.membership.totalAmount)} paid
              </RowSub>
            )}
          </View>
        </Card>

        {/* Unpaid match fees */}
        <Section>
          <SectionLabel>Unpaid match fees ({unpaidMatch.length})</SectionLabel>
        </Section>
        {unpaidMatch.length === 0 ? (
          <Card><EmptyText>No unpaid match fees</EmptyText></Card>
        ) : (
          <Card>
            {unpaidMatch.map((f, i) => (
              <Row key={f.id + i} hasBorder={i < unpaidMatch.length - 1}>
                <RowMain>
                  <RowTitle>{f.matchName || "Match"}</RowTitle>
                  <RowSub>{f.matchDate ? formatDate(f.matchDate) : ""}</RowSub>
                </RowMain>
                <PendingBadge><PendingText>{pence(f.amount)}</PendingText></PendingBadge>
              </Row>
            ))}
          </Card>
        )}

        {/* Unpaid training */}
        {unpaidTraining.length > 0 && (
          <>
            <Section>
              <SectionLabel>Unpaid training ({unpaidTraining.length})</SectionLabel>
            </Section>
            <Card>
              {unpaidTraining.map((f, i) => (
                <Row key={f.id + i} hasBorder={i < unpaidTraining.length - 1}>
                  <RowMain>
                    <RowTitle>{f.matchName || "Training"}</RowTitle>
                    <RowSub>{f.matchDate ? formatDate(f.matchDate) : ""}</RowSub>
                  </RowMain>
                  <PendingBadge><PendingText>{pence(f.amount)}</PendingText></PendingBadge>
                </Row>
              ))}
            </Card>
          </>
        )}

        {/* Paid match fees */}
        {paidFees.length > 0 && (
          <>
            <Section>
              <SectionLabel>Paid ({paidFees.length}) — {pence(totalPaid)}</SectionLabel>
            </Section>
            <Card>
              {paidFees.slice(0, 10).map((f, i) => {
                const isLast = i === Math.min(paidFees.length, 10) - 1;
                return (
                  <Row key={f.id + i} hasBorder={!isLast}>
                    <RowMain>
                      <RowTitle>{f.matchName || "Match"}</RowTitle>
                      <RowSub>{f.matchDate ? formatDate(f.matchDate) : ""}</RowSub>
                    </RowMain>
                    <PaidBadge><PaidText>{pence(f.amount)}</PaidText></PaidBadge>
                  </Row>
                );
              })}
            </Card>
          </>
        )}

        {/* Remind button */}
        {totalOwed > 0 && (
          <Pressable
            onPress={handleRemind}
            disabled={reminding}
            style={({ pressed }) => ({ opacity: pressed || reminding ? 0.85 : 1 })}
          >
            <RemindBtnLarge>
              {reminding ? (
                <ActivityIndicator color="#fff" />
              ) : (
                <RemindBtnText>Send reminder email</RemindBtnText>
              )}
            </RemindBtnLarge>
          </Pressable>
        )}

        <View style={{ height: 40 }} />
      </ContentWidth>
      <AlertSheet {...alertProps} />
    </ScrollView>
  );
}
